import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart2,
  Building2,
  CalendarDays,
  CheckSquare,
  Flag,
  FolderKanban,
  User,
  X,
  Zap,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import type { CalendarEvent } from "../types";

interface EventPopoverProps {
  event: CalendarEvent;
  position: { top: number; left: number };
  onClose: () => void;
}

function formatDateTimeRange(startISO: string, endISO: string): string {
  const start = new Date(startISO);
  const end = new Date(endISO);
  const sameDay =
    start.toDateString() === end.toDateString();
  const dateStr = start.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const startT = start.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });
  const endT = end.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });
  return sameDay ? `${dateStr} · ${startT} – ${endT}` : `${dateStr} · ${startT}`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function typeLabel(type: CalendarEvent["type"]): string {
  return type.charAt(0).toUpperCase() + type.slice(1);
}

function TypeIcon({ type }: { type: CalendarEvent["type"] }) {
  const cls = "h-4 w-4";
  if (type === "meeting") return <CalendarDays className={cls} strokeWidth={1.5} />;
  if (type === "task") return <CheckSquare className={cls} strokeWidth={1.5} />;
  if (type === "action") return <Zap className={cls} strokeWidth={1.5} />;
  return <Flag className={cls} strokeWidth={1.5} />;
}

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className="inline-flex items-center rounded-full bg-[hsl(210,30%,93%)] px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-[hsl(215,16%,40%)]"
    >
      {String(status).replace(/_/g, " ")}
    </span>
  );
}

function isOverdue(dueIso: string | null | undefined, status: string): boolean {
  if (!dueIso) return false;
  const completedish = ["COMPLETED", "CANCELLED", "completed", "cancelled"];
  if (completedish.includes(status)) return false;
  return new Date(dueIso).getTime() < Date.now();
}

export function EventPopover({ event, position, onClose }: EventPopoverProps) {
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) onClose();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  // Smart positioning: keep within viewport
  const POPOVER_WIDTH = 280;
  const POPOVER_HEIGHT_ESTIMATE = 280;
  let left = position.left;
  let top = position.top;
  if (typeof window !== "undefined") {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    if (left + POPOVER_WIDTH + 16 > vw) left = vw - POPOVER_WIDTH - 16;
    if (left < 8) left = 8;
    if (top + POPOVER_HEIGHT_ESTIMATE + 16 > vh)
      top = Math.max(8, vh - POPOVER_HEIGHT_ESTIMATE - 16);
  }

  return (
    <div
      ref={ref}
      className="fixed z-[60] w-[280px] rounded-xl border bg-white shadow-xl"
      style={{
        top,
        left,
        borderColor: "hsl(210,30%,85%)",
      }}
    >
      <div
        className="flex items-start gap-2 rounded-t-xl px-3 pt-3 pb-2"
        style={{
          borderLeft: `4px solid ${event.color}`,
          paddingLeft: 12,
        }}
      >
        <div
          className="flex h-6 w-6 items-center justify-center rounded"
          style={{ background: event.color + "22", color: event.color }}
        >
          <TypeIcon type={event.type} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
            {typeLabel(event.type)}
          </div>
          <div className="text-[14px] font-semibold text-foreground leading-tight truncate">
            {event.title}
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            {event.metadata.code}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(210,30%,95%)] hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" strokeWidth={1.5} />
        </button>
      </div>

      <div className="space-y-1.5 px-3 pb-3 text-[12px]">
        {event.type === "meeting" && (
          <>
            <Row icon={<CalendarDays className="h-3.5 w-3.5" />}>
              {formatDateTimeRange(event.start, event.end)}
            </Row>
            {event.metadata.organizer && (
              <Row icon={<User className="h-3.5 w-3.5" />}>
                {event.metadata.organizer}
              </Row>
            )}
          </>
        )}

        {event.type === "task" && (
          <>
            <Row icon={<CalendarDays className="h-3.5 w-3.5" />}>
              <span
                className={
                  isOverdue(event.metadata.dueDate, event.status)
                    ? "text-[hsl(0,72%,45%)] font-medium"
                    : ""
                }
              >
                Due {event.metadata.dueDate ? formatDate(event.metadata.dueDate) : "—"}
                {isOverdue(event.metadata.dueDate, event.status) ? " (overdue)" : ""}
              </span>
            </Row>
            {event.assigneeName && (
              <Row icon={<User className="h-3.5 w-3.5" />}>
                {event.assigneeName}
              </Row>
            )}
            {event.priority && (
              <Row icon={<Flag className="h-3.5 w-3.5" />}>
                Priority: {String(event.priority).replace(/_/g, " ")}
              </Row>
            )}
          </>
        )}

        {event.type === "action" && (
          <>
            <Row icon={<CalendarDays className="h-3.5 w-3.5" />}>
              <span
                className={
                  isOverdue(event.metadata.dueDate, event.status)
                    ? "text-[hsl(0,72%,45%)] font-medium"
                    : ""
                }
              >
                Due {event.metadata.dueDate ? formatDate(event.metadata.dueDate) : "—"}
                {isOverdue(event.metadata.dueDate, event.status) ? " (overdue)" : ""}
              </span>
            </Row>
            {event.assigneeName && (
              <Row icon={<User className="h-3.5 w-3.5" />}>
                {event.assigneeName}
              </Row>
            )}
            {event.priority && (
              <Row icon={<Zap className="h-3.5 w-3.5" />}>
                Priority: {String(event.priority).replace(/_/g, " ")}
              </Row>
            )}
          </>
        )}

        {event.type === "milestone" && (
          <>
            <Row icon={<CalendarDays className="h-3.5 w-3.5" />}>
              Due {event.metadata.dueDate ? formatDate(event.metadata.dueDate) : "—"}
            </Row>
            {event.assigneeName && (
              <Row icon={<User className="h-3.5 w-3.5" />}>{event.assigneeName}</Row>
            )}
            {typeof event.metadata.progress === "number" && (
              <div className="pt-1">
                <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                  <span className="flex items-center gap-1.5">
                    <BarChart2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                    Progress
                  </span>
                  <span>{event.metadata.progress}%</span>
                </div>
                <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-[hsl(210,30%,92%)]">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${Math.min(100, Math.max(0, event.metadata.progress))}%`,
                      background: event.color,
                    }}
                  />
                </div>
              </div>
            )}
          </>
        )}

        {(event.projectName || event.companyName) && (
          <div className="mt-2 border-t pt-2" style={{ borderColor: "hsl(210,30%,90%)" }}>
            {event.projectName && (
              <Row icon={<FolderKanban className="h-3.5 w-3.5" />}>
                {event.projectName}
              </Row>
            )}
            {event.companyName && (
              <Row icon={<Building2 className="h-3.5 w-3.5" />}>
                {event.companyName}
              </Row>
            )}
          </div>
        )}

        <div className="pt-2">
          <StatusBadge status={event.status} />
        </div>
      </div>

      <div
        className="flex items-center justify-end gap-2 rounded-b-xl border-t px-3 py-2"
        style={{ borderColor: "hsl(210,30%,90%)" }}
      >
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-[12px]"
          onClick={() => navigate(event.url + "?edit=1")}
        >
          Edit
        </Button>
        <Button
          size="sm"
          className="h-7 text-[12px]"
          onClick={() => navigate(event.url)}
        >
          View Details →
        </Button>
      </div>
    </div>
  );
}

function Row({
  icon,
  children,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-2 text-[hsl(215,16%,30%)]">
      <span className="mt-0.5 text-muted-foreground">{icon}</span>
      <span className="flex-1 min-w-0 truncate">{children}</span>
    </div>
  );
}
